<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">
    <h1 class="text-[22px] sm:text-[24px] font-bold text-gray-900 mb-6">Purchasing Fund</h1>

    <div v-if="!can.view" class="mt-10"></div>

    <template v-else>
      <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 items-end gap-12 mb-6">
        <div>
          <div class="text-sm font-semibold text-gray-800 mb-2">Company</div>
          <select v-model="selectedCompany" class="w-full max-w-[320px] h-10 rounded-xl border-[2px] border-gray-300 px-3 text-sm focus:outline-none">
            <option disabled value="">Select company</option>
            <option v-for="c in companies" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>

        <div>
          <div class="text-sm font-semibold text-gray-800 mb-2">Petty Cash</div>
          <select v-model="selectedPettyCash" class="w-full max-w-[320px] h-10 rounded-xl border-[2px] border-gray-300 px-3 text-sm focus:outline-none">
            <option disabled value="">Select petty cash</option>
            <option v-for="p in pettyCashList" :key="p.id" :value="p.id">{{ p.label }}</option>
          </select>
        </div>

        <div class="flex md:justify-end">
          <button
            v-if="can.createPettyCash"
            type="button"
            class="inline-flex items-center gap-2 h-10 px-4 rounded-full text-white font-bold text-sm shadow-sm hover:opacity-95"
            :style="{ backgroundColor: '#5B9AC5' }"
            @click="showAddPettyCash = true"
          >
            + New Petty Cash
          </button>
        </div>
      </div>

      <div v-if="showTable" class="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <DynamicTable
          :rows="rows"
          :columns="columns"
          :rows-per-page="50"
          :rows-per-page-options="[10, 20, 50]"
          :show-header="true"
          search-placeholder="Search"
        >
          <template #header-actions>
            <button
              v-if="can.viewFund"
              type="button"
              class="inline-flex items-center gap-2 h-9 px-4 rounded-full text-white font-bold text-sm shadow-sm hover:opacity-95"
              :style="{ backgroundColor: '#5B9AC5' }"
              @click="newPurchasingFund"
            >
              + New Purchasing Fund
            </button>
          </template>

          <!-- Created By (read-only) -->
          <template #created_by="{ row }">
            <span class="text-gray-700 text-xs">{{ row.createdBy || '—' }}</span>
          </template>

          <!-- Approved By → dots (open modal) or dashes -->
          <template #approved_by="{ row }">
            <div class="flex items-center justify-start pl-2">
              <button
                v-if="row.approvers && row.approvers.length"
                type="button"
                class="text-gray-500 hover:text-gray-700 leading-none text-lg font-bold"
                @click="openApprovers(row)"
                aria-label="View approvers"
                title="View approvers"
              >⋮</button>
              <span v-else class="text-gray-400 select-none">-- --</span>
            </div>
          </template>

          <template #action="{ row }">
            <div class="flex items-center gap-3 text-sm">
              <!-- Approve (only if current user hasn't approved yet) -->
              <button
                v-if="can.approve && !row.approvedByMe"
                type="button"
                class="inline-flex items-center justify-center px-3 h-7 rounded-md bg-green-500 text-white font-medium hover:bg-green-600"
                @click="approveRow(row)"
              >
                Approve
              </button>

              <button v-if="can.viewFund" class="inline-flex items-center gap-1 text-gray-700 hover:text-gray-900" @click="viewRow(row)">
                <i class="far fa-eye text-xs mt-1"></i><span>View</span>
              </button>

              <button v-if="can.edit" class="inline-flex items-center gap-1 text-gray-700 hover:text-gray-900" @click="editRow(row)">
                <i class="fas fa-pen text-xs mt-1"></i><span>Edit</span>
              </button>

              <button v-if="can.delete" class="inline-flex items-center gap-1 text-black hover:text-red-700" @click="deleteRow(row)">
                <i class="fas fa-trash-alt text-black text-xs mt-1"></i><span>Delete</span>
              </button>

              <span v-if="!can.viewFund && !can.edit && !can.delete" class="text-gray-400">—</span>
            </div>
          </template>
        </DynamicTable>
      </div>

      <!-- Approvers modal (read-only list of who approved) -->
      <transition name="fade">
        <div
          v-if="approversModal.open"
          class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          @click.self="closeApprovers"
        >
          <div class="bg-white w-full max-w-sm rounded-2xl p-6 relative">
            <button
              class="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              @click="closeApprovers"
              aria-label="Close"
            >✕</button>

            <div class="space-y-6 pt-2">
              <p v-if="!approversModal.names.length" class="text-center text-sm text-gray-400">
                No approvers yet.
              </p>

              <div
                v-for="n in approversModal.names"
                :key="n"
                class="text-center text-gray-800 font-semibold text-lg select-none"
              >
                {{ n }}
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- Add Petty Cash Modal -->
      <div v-if="showAddPettyCash" class="fixed inset-0 z-[1000] flex items-center justify-center">
        <div class="absolute inset-0 bg-black/50" @click="showAddPettyCash = false"></div>
        <div class="relative bg-white w-[92%] max-w-[460px] rounded-2xl shadow-lg p-6 sm:p-8">
          <button
            type="button"
            class="absolute top-4 right-4 text-gray-600 hover:text-gray-800"
            @click="showAddPettyCash = false"
            aria-label="Close"
          >✕</button>

          <h3 class="text-center text-xl sm:text-2xl font-semibold text-gray-900 mb-6">Add PettyCash</h3>

          <div class="space-y-4">
            <div>
              <div class="text-sm font-semibold text-gray-800 mb-1">Employee</div>
              <input
                v-model="pettyForm.employee_name"
                type="text"
                placeholder="Enter employee name"
                class="w-full h-10 rounded-xl border border-gray-300 px-3 text-sm focus:outline-none"
              />
            </div>

            <div>
              <div class="text-sm font-semibold text-gray-800 mb-1">Company</div>
              <select v-model="pettyForm.company_id" class="w-full h-10 rounded-xl border border-gray-300 px-3 text-sm focus:outline-none">
                <option disabled value="">Select Company</option>
                <option v-for="c in companies" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>

            <button
              type="button"
              class="w-full h-10 rounded-full text-white font-semibold shadow-sm hover:opacity-95"
              :style="{ backgroundColor: '#5B9AC5' }"
              @click="addPettyCash"
            >
              Add
            </button>
          </div>
        </div>
      </div>

      <DeleteModal
        v-model="showDeleteModal"
        :title="'Are you sure?'"
        :message="'Delete this record?'"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
      />
    </template>
  </div>
</template>

<script>
import api from '@/api'
import DynamicTable from '@/components/DynamicTable.vue'
import DeleteModal  from '@/components/DeleteModal.vue'
import Swal from 'sweetalert2'
import { loadMyPermissions, hasAny } from '@/utils/permissions'

// Session-storage key used to persist selections across navigation
const STORAGE_KEY = 'pf_list_selections'

export default {
  name: 'PurchasingFund',
  components: { DynamicTable, DeleteModal },

  data() {
    return {
      can: {
        view:             false,
        createPettyCash:  false,
        viewFund:         false,
        edit:             false,
        delete:           false,
        approve:          false,
        allowedCompanies: null,
      },

      selectedCompany:   '',
      selectedPettyCash: '',
      showAddPettyCash:  false,
      pettyForm: { employee_name: '', company_id: '' },

      companies:       [],
      pettyCashList:   [],

      approversModal: { open: false, names: [] },

      columns: [
        { key: 'no',           label: 'No.',            width: '55px'  },
        { key: 'invoice_no',   label: 'Invoice No.',    width: '95px'  },
        { key: 'supplier_name',label: 'Supplier Name',  width: '220px' },
        { key: 'vehicle_no',   label: 'Vehicle No.',    width: '95px'  },
        { key: 'invoice_date', label: 'Invoice Date',   width: '100px' },
        { key: 'amount',       label: 'Invoice Amount', width: '105px', align: 'center' },
        { key: 'created_by',   label: 'Created By',      width: '145px' },
        { key: 'approved_by',  label: 'Approved By',    width: '145px' },
        { key: 'action',       label: 'Action',         width: '260px' },
      ],

      rows: [],

      showDeleteModal: false,
      rowToDelete:     null,
    }
  },

  computed: {
    authConfig() {
      const token = localStorage.getItem('token')
      return {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    },

    showTable() {
      return !!this.selectedCompany && !!this.selectedPettyCash
    },
  },

  watch: {
  selectedCompany(val) {
    this.selectedPettyCash = ''
    this.pettyCashList = []
    this.rows = []

    if (val) {
      this.fetchPettyCashList(val)
    }

    this._saveSelections()
  },

  selectedPettyCash(val) {
    this._saveSelections()

    if (val && this.selectedCompany) {
      this.fetchRows()
    } else {
      this.rows = []
    }
  },

  showTable(val) {
    if (val) {
      this.fetchRows()
    }
  },
},

  async created() {
    await this.ensurePermissions()
    if (!this.can.view) return
    await this.fetchCompanies()
    this._restoreSelections()
  },
    beforeUnmount() {
      if (this.$route.name !== 'PurchasingFundForm') {
        try {
          sessionStorage.removeItem(STORAGE_KEY)
        } catch (_) {}
      }
    },
    beforeRouteLeave(to, from, next) {
      document.body.style.overflow = ''
      this.approversModal.open = false
      this.showDeleteModal = false

      if (to.name !== 'PurchasingFundForm') {
        try {
          sessionStorage.removeItem(STORAGE_KEY)
        } catch (_) {}
      }

      next()
    },

  methods: {
    // ─── Persist / restore selections ────────────────────────────────────────

    _saveSelections() {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          company:   this.selectedCompany,
          pettyCash: this.selectedPettyCash,
        }))
      } catch (_) {}
    },

    async _restoreSelections() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY)
        if (!raw) return
        const { company, pettyCash } = JSON.parse(raw)

        if (company && this.companies.some(c => c.id === company)) {
          await this.fetchPettyCashList(company)

          this.selectedCompany = company

          if (pettyCash && this.pettyCashList.some(p => p.id === pettyCash)) {
            this.$nextTick(() => {
              this.selectedPettyCash = pettyCash
            })
          }
        }
      } catch (_) {}
    },

    // ─── Permissions ─────────────────────────────────────────────────────────

    async ensurePermissions() {
      try {
        const res       = await api.get('/accounting/permissions/nav', this.authConfig)
        const nav       = res.data?.permissions || res.data || {}
        const pfSection = nav?.['Procurement Management']?.['Purchasing Fund'] || {}
        const granted   = pfSection.granted || []

        const has = (perm) => granted.includes(perm)

        this.can.view            = has('procurement_management.purchasing_fund.view')
        this.can.createPettyCash = has('procurement_management.purchasing_fund.new_petty_cash.create')
        this.can.viewFund        = has('procurement_management.purchasing_fund.create')
        this.can.edit            = has('procurement_management.purchasing_fund.purchasing_fund.edit')
        this.can.delete          = has('procurement_management.purchasing_fund.purchasing_fund.delete')
        this.can.approve         = has('procurement_management.purchasing_fund.purchasing_fund.approved')

        const prefix = 'procurement_management.purchasing_fund.company.'
        const suffix = '.view'
        const pfAll   = pfSection.all || []

        const allCompanyPerms     = pfAll.filter(p => p.startsWith(prefix) && p.endsWith(suffix))
        const grantedCompanyPerms = granted.filter(p => p.startsWith(prefix) && p.endsWith(suffix))

        if (allCompanyPerms.length === 0) {
          this.can.allowedCompanies = null
        } else {
          this.can.allowedCompanies = grantedCompanyPerms
            .map(p => p.slice(prefix.length, -suffix.length))
        }

      } catch (err) {
        console.error('[PurchasingFund] ensurePermissions failed:', err)
        this.can = {
          view: false, createPettyCash: false, viewFund: false,
          edit: false, delete: false, approve: false, allowedCompanies: null,
        }
      }
    },

    // ─── Data fetching ────────────────────────────────────────────────────────

    formatAmount(value) {
      const num = parseFloat(value)
      if (isNaN(num)) return value
      return Number(num).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 3 })
    },

    async fetchCompanies() {
      try {
        const res  = await api.get('/accounting/companies', this.authConfig)
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || [])
        let companies = data.map(c => ({ id: c.id, name: c.name }))

        if (this.can.allowedCompanies !== null) {
          companies = companies.filter(c => this.can.allowedCompanies.includes(c.name))
        }

        this.companies = companies
      } catch (e) {
        console.error('Error fetching companies', e)
      }
    },

    async fetchPettyCashList(companyId) {
      try {
        const res  = await api.get('/accounting/purchasing-fund/petty-cash', {
          ...this.authConfig,
          params: { company_id: companyId },
        })
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || [])
        this.pettyCashList = data.map(u => ({
          id:    u.id,
          label: u.employee_name || `User #${u.id}`,
        }))
      } catch (e) {
        console.error('Error fetching petty cash list', e)
      }
    },

    async fetchRows() {
      if (!this.can.viewFund) return
      try {
        const res  = await api.get('/accounting/purchasing-fund', {
          ...this.authConfig,
          params: { petty_cash_id: this.selectedPettyCash },
        })
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || [])

        this.rows = data.map((item, index) => {
          // approvers can come as approved_by[] / approvers[] / legacy single name
          const raw = item.approved_by ?? item.approvers ?? item.approved_by_name ?? []
          const approvers = Array.isArray(raw)
            ? raw.filter(Boolean)
            : (raw ? [raw] : [])

          // backend may send it as approved_me or approved_by_me
          const approvedMeRaw = item.approved_by_me ?? item.approved_me

          return {
            id:               item.id,
            no:               item.company_sequence ?? (index + 1),  // ← company_sequence
            company_sequence: item.company_sequence ?? (index + 1),  // ← keep for navigation
            invoice_no:       item.invoice_number,
            supplier_name:    item.supplier_name,
            vehicle_no:       item.vehicle_number,
            invoice_date:     item.invoice_date,
            amount:           this.formatAmount(item.total_amount),
            // who created the record (fallback to legacy waiting_by_name until backend adds created_by)
            createdBy:        item.created_by || item.created_by_name || item.waiting_by_name || '',
            // names shown in the "Approved By" modal
            approvers,
            // has the current logged-in user already approved?
            approvedByMe:     approvedMeRaw != null ? !!approvedMeRaw : false,
          }
        })
      } catch (e) {
        console.error('Error fetching purchasing fund rows', e)
        this.rows = []
      }
    },

    // ─── Approvers modal ──────────────────────────────────────────────────────

    openApprovers(row) {
      this.approversModal = { open: true, names: row.approvers || [] }
      document.body.style.overflow = 'hidden'
    },

    closeApprovers() {
      this.approversModal = { open: false, names: [] }
      document.body.style.overflow = ''
    },

    // ─── Approve action (current user approves the row) ───────────────────────

    async approveRow(row) {
      if (!this.can.approve) return
      try {
        await api.post(
          `/accounting/purchasing-fund/${row.id}/approve`,
          {},
          this.authConfig
        )

        Swal.fire({ icon: 'success', title: 'Approved', toast: true, position: 'top', timer: 2000, showConfirmButton: false })

        // refetch so approvers list + approved_by_me come back correct
        await this.fetchRows()
      } catch (e) {
        console.error('Error approving', e)
        const msg = e.response?.data?.error || e.response?.data?.message || 'Failed to approve.'
        Swal.fire({ icon: 'error', title: 'Error', text: msg, toast: true, position: 'top', timer: 2500, showConfirmButton: false })
      }
    },

    // ─── Navigation ──────────────────────────────────────────────────────────

    newPurchasingFund() {
      if (!this.can.viewFund) return
      const company = this.companies.find(c => c.id === this.selectedCompany)
      this.$router.push({
        name: 'PurchasingFundForm',
        state: {
          pfMode:        'add',
          pfPettyCashId: String(this.selectedPettyCash),
          pfCompanyId:   String(this.selectedCompany),
          pfCompanyName: company?.name || '',
        },
      })
    },

    editRow(row) {
      if (!this.can.edit) return
      this.$router.push({
        name: 'PurchasingFundForm',
        state: {
          pfMode:     'edit',
          pfId:       String(row.id),
          pfSequence: String(row.company_sequence ?? ''),  // ← pass sequence
        },
      })
    },

    viewRow(row) {
      if (!this.can.viewFund) return
      this.$router.push({
        name: 'PurchasingFundForm',
        state: {
          pfMode:     'view',
          pfId:       String(row.id),
          pfSequence: String(row.company_sequence ?? ''),  // ← pass sequence
        },
      })
    },

    // ─── Delete ───────────────────────────────────────────────────────────────

    deleteRow(row) {
      if (!this.can.delete) return
      this.rowToDelete     = row
      this.showDeleteModal = true
      document.body.style.overflow = 'hidden'
    },

    cancelDelete() {
      this.rowToDelete     = null
      this.showDeleteModal = false
      document.body.style.overflow = ''
    },

    async confirmDelete() {
      if (!this.rowToDelete) return
      try {
        await api.delete(`/accounting/purchasing-fund/${this.rowToDelete.id}`, this.authConfig)
        this.rows = this.rows.filter(r => r.id !== this.rowToDelete.id)
        Swal.fire({ icon: 'success', title: 'Deleted', toast: true, position: 'top', timer: 2000, showConfirmButton: false })
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'Error', text: e.response?.data?.message || 'Failed to delete.', toast: true, position: 'top', timer: 2500, showConfirmButton: false })
      }
      this.cancelDelete()
    },

    // ─── Add Petty Cash ───────────────────────────────────────────────────────

    async addPettyCash() {
      if (!this.can.createPettyCash) return
      if (!this.pettyForm.employee_name || !this.pettyForm.company_id) return
      try {
        await api.post('/accounting/purchasing-fund/petty-cash', this.pettyForm, this.authConfig)
        Swal.fire({ icon: 'success', title: 'Added', toast: true, position: 'top', timer: 2000, showConfirmButton: false })
        if (this.pettyForm.company_id === this.selectedCompany) {
          this.fetchPettyCashList(this.selectedCompany)
        }
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'Error', text: e.response?.data?.message || 'Failed to add petty cash.', toast: true, position: 'top', timer: 2500, showConfirmButton: false })
      }
      this.showAddPettyCash = false
      this.pettyForm = { employee_name: '', company_id: '' }
    },
  },
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity .15s ease; }
.fade-enter-from,  .fade-leave-to      { opacity: 0; }
:deep(table)  { @apply min-w-[1200px]; }
:deep(th),
:deep(td)     { @apply whitespace-nowrap align-middle; font-size: 0.95rem; }
</style>
